/**
 * Bring-your-own-key storage.
 *
 * - A NON-extractable AES-GCM 256 CryptoKey is generated once and persisted (as a
 *   CryptoKey object, via structured clone) in IndexedDB database `icd-byok`.
 *   Its raw bytes can never be read by JavaScript — including by this app.
 * - API keys are encrypted with that key; only ciphertext + IV (base64) are kept in
 *   localStorage under `icd-byok-v1`, together with non-secret settings
 *   (provider, model, storage mode, last 4 characters for display).
 * - Keys are stored per provider so switching providers does not lose other keys.
 */

const STORAGE_KEY = 'icd-byok-v1';
const DB_NAME = 'icd-byok';
const DB_STORE = 'keys';
const MASTER_ID = 'master';
const CHANGE_EVENT = 'icd-byok-change';

export const PROVIDERS = ['openai', 'anthropic', 'gemini', 'openrouter'];
export const EMBEDDING_SLOT = '__embedding';

export class ByokError extends Error {
  constructor(message, code = 'BYOK_ERROR') {
    super(message);
    this.name = 'ByokError';
    this.code = code;
  }
}

/* ------------------------------------------------------------------ helpers */

function hasWindow() {
  return typeof window !== 'undefined';
}

/** Whether this browser context can encrypt keys locally. */
export function isByokSupported() {
  return (
    hasWindow() &&
    window.isSecureContext !== false &&
    !!(window.crypto && window.crypto.subtle && typeof window.crypto.subtle.encrypt === 'function') &&
    !!window.indexedDB
  );
}

export function byokUnsupportedReason() {
  if (!hasWindow()) return 'Not running in a browser.';
  if (window.isSecureContext === false) {
    return 'Secure key storage needs a secure (HTTPS) connection. Open the site over https:// (or localhost) to save an API key in this browser.';
  }
  if (!(window.crypto && window.crypto.subtle)) return 'This browser does not support the Web Crypto API, so API keys cannot be stored securely.';
  if (!window.indexedDB) return 'IndexedDB is unavailable (private browsing or storage disabled), so API keys cannot be stored securely in this browser.';
  return '';
}

function assertSupported() {
  if (!isByokSupported()) throw new ByokError(byokUnsupportedReason(), 'BYOK_UNAVAILABLE');
}

function toB64(buf) {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i += 1) s += String.fromCharCode(bytes[i]);
  return window.btoa(s);
}

function fromB64(b64) {
  const s = window.atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i += 1) out[i] = s.charCodeAt(i);
  return out;
}

function readRaw() {
  try {
    const raw = hasWindow() ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function emptyState() {
  return { v: 1, provider: null, model: null, mode: 'browser', keys: {}, embedding: null, updatedAt: null };
}

function readState() {
  const s = readRaw();
  if (!s) return emptyState();
  return {
    ...emptyState(),
    ...s,
    keys: s.keys && typeof s.keys === 'object' ? s.keys : {},
    mode: s.mode === 'vault' ? 'vault' : 'browser',
  };
}

function writeState(state) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, updatedAt: new Date().toISOString() }));
  } catch {
    throw new ByokError('Could not write to browser storage (it may be full or disabled).', 'BYOK_UNAVAILABLE');
  }
  notify();
}

function notify() {
  if (!hasWindow()) return;
  try {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: loadAiSettings() }));
  } catch {
    // CustomEvent unsupported — ignore.
  }
}

/* --------------------------------------------------------------- IndexedDB */

function openDb() {
  return new Promise((resolve, reject) => {
    let req;
    try {
      req = window.indexedDB.open(DB_NAME, 1);
    } catch (e) {
      reject(new ByokError('IndexedDB is unavailable in this browser.', 'BYOK_UNAVAILABLE'));
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(DB_STORE)) db.createObjectStore(DB_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(new ByokError('Could not open secure key storage (IndexedDB).', 'BYOK_UNAVAILABLE'));
    req.onblocked = () => reject(new ByokError('Secure key storage is blocked by another open tab. Close other tabs and retry.', 'BYOK_UNAVAILABLE'));
  });
}

function idbRequest(db, mode, fn) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, mode);
    const store = tx.objectStore(DB_STORE);
    const req = fn(store);
    let result;
    req.onsuccess = () => {
      result = req.result;
    };
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error || req.error);
    tx.onabort = () => reject(tx.error || req.error);
  });
}

let masterKeyPromise = null;

async function loadOrCreateMasterKey(create) {
  const db = await openDb();
  try {
    const existing = await idbRequest(db, 'readonly', (s) => s.get(MASTER_ID));
    if (existing && existing.type === 'secret') return existing;
    if (!create) return null;
    const key = await window.crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
    await idbRequest(db, 'readwrite', (s) => s.put(key, MASTER_ID));
    return key;
  } finally {
    db.close();
  }
}

async function getMasterKey(create = true) {
  assertSupported();
  if (!masterKeyPromise) {
    masterKeyPromise = loadOrCreateMasterKey(create).catch((e) => {
      masterKeyPromise = null;
      if (e instanceof ByokError) throw e;
      throw new ByokError('Secure key storage failed. Your browser may block IndexedDB in this mode.', 'BYOK_UNAVAILABLE');
    });
  }
  const key = await masterKeyPromise;
  if (!key) masterKeyPromise = null;
  return key;
}

async function encryptSecret(secret, slot) {
  const key = await getMasterKey(true);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const data = new TextEncoder().encode(secret);
  const ct = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: new TextEncoder().encode(`icd-byok:${slot}`) }, key, data);
  return { ct: toB64(ct), iv: toB64(iv), last4: secret.slice(-4), savedAt: new Date().toISOString() };
}

async function decryptSecret(entry, slot) {
  if (!entry || !entry.ct || !entry.iv) return null;
  const key = await getMasterKey(false);
  if (!key) {
    throw new ByokError('Your saved API key can no longer be decrypted because browser storage was cleared. Please re-enter it in Settings.', 'BYOK_DECRYPT');
  }
  try {
    const pt = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: fromB64(entry.iv), additionalData: new TextEncoder().encode(`icd-byok:${slot}`) },
      key,
      fromB64(entry.ct)
    );
    return new TextDecoder().decode(pt);
  } catch {
    throw new ByokError('Your saved API key could not be decrypted. Please re-enter it in Settings.', 'BYOK_DECRYPT');
  }
}

/* ------------------------------------------------------------------ public */

/**
 * Save AI settings. Secrets are optional:
 *   apiKey: string → encrypt & store for `provider`; '' or null → remove that provider's key; undefined → keep.
 *   embeddingKey: same semantics for the optional OpenAI embedding key.
 */
export async function saveAiSettings({ provider, model, apiKey, embeddingKey, mode } = {}) {
  const state = readState();
  const next = { ...state, keys: { ...state.keys } };
  if (provider !== undefined) {
    if (provider && !PROVIDERS.includes(provider)) throw new ByokError(`Unknown provider "${provider}".`, 'BYOK_INVALID');
    next.provider = provider || null;
  }
  if (model !== undefined) next.model = model ? String(model).trim() : null;
  if (mode !== undefined) next.mode = mode === 'vault' ? 'vault' : 'browser';

  const target = next.provider;
  if (apiKey !== undefined) {
    const k = apiKey === null ? '' : String(apiKey).trim();
    if (!target) throw new ByokError('Choose a provider before saving a key.', 'BYOK_INVALID');
    if (k) next.keys[target] = await encryptSecret(k, target);
    else delete next.keys[target];
  }
  if (embeddingKey !== undefined) {
    const k = embeddingKey === null ? '' : String(embeddingKey).trim();
    next.embedding = k ? await encryptSecret(k, EMBEDDING_SLOT) : null;
  }
  writeState(next);
  return loadAiSettings();
}

/** Non-secret view of the settings. */
export function loadAiSettings() {
  const s = readState();
  const keys = {};
  Object.keys(s.keys || {}).forEach((p) => {
    const e = s.keys[p];
    if (e && e.ct) keys[p] = { last4: e.last4 || '', savedAt: e.savedAt || null };
  });
  return {
    provider: s.provider,
    model: s.model,
    mode: s.mode,
    keys,
    hasBrowserKey: !!(s.provider && keys[s.provider]),
    embedding: s.embedding && s.embedding.ct ? { last4: s.embedding.last4 || '', savedAt: s.embedding.savedAt || null } : null,
    updatedAt: s.updatedAt,
    supported: isByokSupported(),
  };
}

/** Decrypt the API key for `provider` (defaults to the selected provider). */
export async function getDecryptedKey(provider) {
  const s = readState();
  const p = provider || s.provider;
  if (!p) return null;
  return decryptSecret(s.keys[p], p);
}

export async function getDecryptedEmbeddingKey() {
  const s = readState();
  if (!s.embedding) return null;
  return decryptSecret(s.embedding, EMBEDDING_SLOT);
}

/**
 * Remove stored keys. `provider` → only that provider's key; 'embedding' → only the
 * embedding key; omitted → every stored key (settings such as provider/model remain).
 */
export function clearAiKey(provider) {
  const s = readState();
  if (provider === 'embedding') {
    s.embedding = null;
  } else if (provider) {
    const keys = { ...s.keys };
    delete keys[provider];
    s.keys = keys;
  } else {
    s.keys = {};
    s.embedding = null;
    if (s.mode === 'vault') s.mode = 'browser';
  }
  writeState(s);
}

/** Synchronous check used to decide whether to show "Add your API key". */
export function hasAiKey() {
  const s = readState();
  if (!s.provider || !s.model) return false;
  if (s.mode === 'vault') return true;
  const e = s.keys[s.provider];
  return !!(e && e.ct && e.iv);
}

/** Subscribe to settings changes (same tab via custom event, other tabs via `storage`). */
export function onAiSettingsChange(cb) {
  if (!hasWindow()) return () => {};
  const handler = () => cb(loadAiSettings());
  const storageHandler = (e) => {
    if (!e || e.key === STORAGE_KEY || e.key === null) handler();
  };
  window.addEventListener(CHANGE_EVENT, handler);
  window.addEventListener('storage', storageHandler);
  return () => {
    window.removeEventListener(CHANGE_EVENT, handler);
    window.removeEventListener('storage', storageHandler);
  };
}
